import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  Component, ElementRef, ViewChild, OnInit, ChangeDetectionStrategy,
  ChangeDetectorRef, ViewEncapsulation, Input
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { mergeMap, debounceTime, map } from 'rxjs/operators';
import { ComponentSetupService } from '../../../../formbird-sc-shared/src/lib/component-setup.service';
import { SearchResultsProcessor } from '@formbird/shared';
import { FormbirdInjectorService } from '../../../../formbird-services/src/lib/services/injector/formbird-injector.service';
import { RelatedDocumentQueries } from './related-document.queries';
import { RelatedDocumentService } from './related-document.service';
import { ChangedDocumentService } from 'formbird-services';
import { DocumentChangedInfo, SharedConstants } from '@formbird/types';
import { DataService } from '../../../../formbird-services/src/lib/services/data/data.service';
import { AbstractDocumentComponent } from '../../../../formbird-sc-shared/src/lib/components/abstract-document.component';
import { RelatedDocument, RelatedDocumentComponentDefinition } from './related-document.types';
import { fromEvent } from 'rxjs';

@Component({
  templateUrl: 'related-document.component.html',
  styleUrls: ['related-document.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class RelatedDocumentComponent extends AbstractDocumentComponent<RelatedDocumentComponentDefinition> implements OnInit {
  @Input() floatLabel: any;

  @ViewChild('input') input: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;

  searchResults;
  dataLoaded = false;
  selectable = true;
  removable = true;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  inputCtrl = new FormControl();
  selected: RelatedDocument[] = [];

  disableLink = false;
  maxSelectedItems;
  showDropDown;
  uniqueSelection;
  mandatory;
  relatedFields;
  options: any = {
    page: 0,
    pageSize: 50,
    text: '',
    from: null,
    sourceFilter: { include: ['documentId', 'systemHeader.summaryName'] }
  };
  itemsPerPage = 50;
  totalItems = 0;
  fromLoadMore = false;

  labelStyle: object = {};
  searchClientService: any;
  pendingOperationService;
  loggedInUserService: any;
  selectionRows= 0;
  windowResizer$ = fromEvent(window, 'resize');

  constructor(
    private relatedDocumentQuries: RelatedDocumentQueries,
    public relatedDocumentService: RelatedDocumentService,
    protected componentSetupService: ComponentSetupService,
    protected formbirdInjectorService: FormbirdInjectorService,
    private dataService: DataService,
    protected cd: ChangeDetectorRef,
    protected changedDocumentService: ChangedDocumentService,
  ) {
    super(formbirdInjectorService, changedDocumentService, componentSetupService, cd);
    this.searchClientService = this.formbirdInjectorService.get('SearchClientService');
    this.pendingOperationService = formbirdInjectorService.get('PendingOperationService');
    this.loggedInUserService = this.formbirdInjectorService.get('LoggedInUserService');
  }

  ngOnInit() {
    this.shouldNotifyCompInitializedInSuper = false;
    this.floatLabel = this.floatLabel ? this.floatLabel : 'auto';
    this.disableLink = this.componentDefinition.disableLink;

    this.maxSelectedItems = this.componentDefinition.singleSelection ? 1 : null;
    this.showDropDown = !!this.componentDefinition.showDropDown;
    this.uniqueSelection = !!this.componentDefinition.uniqueSelection;
    this.mandatory = !!this.componentDefinition.mandatory;

    super.ngOnInit();

    if (this.componentDefinition.relatedFields) {
      this.relatedFields = this.componentDefinition.relatedFields;
      // tslint:disable-next-line:forin
      for (const key in this.relatedFields) {
        this.options.sourceFilter.include.push(this.relatedFields[key]);
      }
    }

    if ( this.componentDefinition.queryOptions?.pageSize){
      this.options.pageSize = this.componentDefinition.queryOptions.pageSize;
      this.itemsPerPage = this.componentDefinition.queryOptions.pageSize;
    }
    if ( this.componentDefinition.selectionRows){
      this.selectionRows = this.componentDefinition.selectionRows;
    }
    const selfThis = this;
    this.windowResizer$
      .pipe(
        map((i: any) => i),
        debounceTime(500) //  waits > 0.5s between 2 events emitted before running the next.
      )
      .subscribe((event) => {
        // console.log('resize is finished');
        selfThis.setScrollView();
      });
    const dataSource = this.searchClientService.getDefaultSearchProvider(this);
    if (this.componentDefinition.queryName) {
      this.options.dataSource = dataSource;
      this.options.queryName = this.componentDefinition.queryName;

      this.options.searchOfflineOnly = this.componentDefinition.searchOptions?.searchOfflineOnly;
    }

    this.searchResults = this.inputCtrl.valueChanges.pipe(
      debounceTime(SharedConstants.DEFAULT_SEARCH_DELAY),
      mergeMap(async (searchKey) => {
        if (!this.componentDefinition.filter) {
          console.log(`sc-related-document ${this.fieldName} does not have a filter field defined - no selections will be possible`);
          return [];
        }

        if (this.maxSelectedItems && this.maxSelectedItems <= this.selected.length) {
          return [];
        }

        this.relatedDocumentQuries.processElasticQuery(this, searchKey, false);
        if (this.searchClientService.isDexieQuery(this)) {
          this.relatedDocumentQuries.processDexieQuery(this, searchKey, false);
        }

        if(this.fromLoadMore){
          const newSize = this.options.pageSize + this.itemsPerPage;
          this.options.pageSize = (newSize > this.totalItems) ? this.totalItems : newSize;
        }

        const data = await this.searchClientService.search(this.options);
        return await new Promise((resolve, reject) => {
          SearchResultsProcessor.processSearchResults(data,
            function elementProcessing(hit) { },
            (hits, totalHits) => {
              this.totalItems = totalHits;
              const results = hits.map((doc: any) => {
                doc.name = doc?.systemHeader?.summaryName || 'Document ' + doc.documentId;
                doc.uniqueSelection = this.uniqueSelection;
                return doc;
              }).filter((doc: any) => {
                if (this.uniqueSelection) {
                  return !this.selected.find(value => value.documentId === doc.documentId);
                }

                return true;
              });

              setTimeout(() => {
                if(this.autocomplete.autocomplete.panel && this.fromLoadMore){
                  this.autocomplete.autocomplete.panel.nativeElement.scrollTop =
                    this.autocomplete.autocomplete.panel.nativeElement.scrollHeight;
                  this.autocomplete.autocomplete.panel.nativeElement.classList.remove('loading');
                }
                this.fromLoadMore = false;
              }, 100);

              resolve(results);
            });
        });
      }));


      if(Array.isArray(this.fieldValue) && this.fieldValue.length > 0){
        this.setValue(this.fieldValue);
      } else if(!Array.isArray(this.fieldValue) && this.fieldValue){
        this.formatDefaultValues().then(formattedValues => {
          if (formattedValues && formattedValues.length > 0) {
              this.setValue(formattedValues);
              this.document[this.fieldName] = formattedValues;
          }
        }).catch(error => {
          console.error('Error formatting default values:', error);
        });
      }


    // this.autocompleteInput.panel.nativeElement.scrollTop = this.myScrollContainer.nativeElement.scrollHeight;

    const compSub = this.changedDocumentService.watchComponentDef(this.fieldName, this.template.documentId).subscribe(tplItem => {
      this.onComponentDefChanged(tplItem);
      this.autocomplete.closePanel();
    });
    this.subs.add(compSub);
    // this.autocomplete

    this.updateLabelStyle();
  }

  async formatDefaultValues() {
    if (this.fieldValue) {
      const fieldValueArray = this.fieldValue.split(',').map(item => item.trim());

      this.relatedDocumentQuries.processElasticQuery(this, "", false);
      if (this.searchClientService.isDexieQuery(this)) {
        this.relatedDocumentQuries.processDexieQuery(this, "", false);
      }

       const searchOptions = {
        filter: {
          query: {
            bool: {
              should: [
                { terms: { documentId: fieldValueArray } },
                { terms: { 'systemHeader.summaryName.lcase': fieldValueArray } }
               ],
              minimum_should_match: 1
            }
          }
        },
        sourceFilter: {
          include: ["documentId", "name", "systemHeader", "serviceVersionNumber"]
        },
        from: 0,
        pageSize: fieldValueArray.length
      };

      const data = await this.searchClientService.search(searchOptions);
      const results = await new Promise<any[]>((resolve, reject) => {
        SearchResultsProcessor.processSearchResults(data,
          function elementProcessing(hit) { },
          (hits, totalHits) => {
            const results = hits.map((doc: any) => {
              doc.name = doc?.systemHeader?.summaryName || 'Document ' + doc.documentId;
              doc.uniqueSelection = this.uniqueSelection;
              return doc;
            });
             resolve(results);
          });
      });

      let formattedValues = [];
      let missingItems = [];

      for (const item of fieldValueArray) {
        const result = results.find(result => result.documentId === item || result.name === item);
        if (result) {
          const formattedValue = {
            documentId: result.documentId,
            name: this.relatedDocumentService.getValueFromKeyString(result, 'systemHeader.summaryName')
          };

          for (const rkey in this.relatedFields) {
            formattedValue[rkey] = this.relatedDocumentService.getValueFromKeyString(result, this.relatedFields[rkey]);
          }

          formattedValues.push(formattedValue);
        } else {
          missingItems.push(item);
        }
      }

      if (missingItems.length > 0) {
        console.error(`Related document component "${this.fieldName}" could not find the following items in the filter:`, missingItems);
       }

      return formattedValues;
    }
  }

  scrollEvent = (event: any): void => {
    if (this.autocomplete.panelOpen) this.autocomplete.updatePosition();
  };
  focusInEvent = (): void => {
    window.addEventListener('scroll', this.scrollEvent, true);
  };
  focusOutEvent = (): void => {
    window.removeEventListener('scroll', this.scrollEvent, true);
  };

  setValue(value) {
    if (!Array.isArray(value)) {
      value = [];
    }

      if (this.uniqueSelection) {
      // this.selected = <RelatedDocument[]>[...new Map(value.map(item => [item.name, item])).values()];
      this.selected = ([...new Map(value.map(item => [item.name, item])).values()] as RelatedDocument[]);
    } else {
      this.selected = [...value];
    }
    this.setScrollView();
    this.detectChanges();
  }

  initializeFieldValue() {
    // 15019: Angular Only: initialize for Parent Doc
    if (typeof this.fieldValue === 'string') {
      const item = {
        documentId: this.fieldValue,
        name: this.fieldName
      };

      this.onFieldValueChanged([item]);
    }
    this.setScrollView();
  }

  onFieldValueChanged(newValue) {
    if(Array.isArray(newValue)) {
      this.setValue(newValue);
    }else{
      this.formatDefaultValues().then(formattedValues => {
        if (formattedValues) {
            this.setValue(formattedValues);
        }
      }).catch(error => {
        console.error('Error formatting default values:', error);
        this.setValue(this.fieldValue);
      });
    }
  }

    removeChip(item: RelatedDocument): void {
         this.input.nativeElement.click();
         this.input.nativeElement.focus();
    }

    clear() {
        if (this.disabled) return;
        this.fieldValue = [];
        this.setValue(this.fieldValue);
        this.notifyDocumentChanged(this.fieldName, this.selected);
    }

  onClick() {
    if (this.showDropDown && !this.disabled) {
      // set text to input to execute search
      this.inputCtrl.setValue('');
      this.inputCtrl.markAllAsTouched();
    }
  }

  notifyDocumentChanged(fieldName, fieldValue) {
    const documentChangedInfo: DocumentChangedInfo = {
      documentId: this.document.documentId,
      fieldName: fieldName,
      newValue: fieldValue,
      isInitValue: false,
      formParameters: this.formParameters
    };

    this.changedDocumentService.notifyDocumentChanged(documentChangedInfo);
  }

  fieldDefaultValueRemove() {
    if (this.componentDefinition.fieldDefault) {
      Object.keys(this.componentDefinition.fieldDefault).map((destFld) => {
        this.notifyDocumentChanged(destFld, null);
      });
    }
  }

  onRemove(item: RelatedDocument): void {
    const index = this.selected.indexOf(item);
    if (index >= 0) {
      this.selected.splice(index, 1);
    }

    this.fieldDefaultValueRemove();
    this.notifyDocumentChanged(this.fieldName, this.selected);
  }

  loadMore() {
    this.fromLoadMore = true;
    this.autocomplete.autocomplete.panel.nativeElement.classList.add('loading');
    this.inputCtrl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  async onSelect(event: MatAutocompleteSelectedEvent): Promise<void> {
    if (this.maxSelectedItems && this.selected.length >= this.maxSelectedItems) {
      return;
    }

    const selectedValue = event.option.value;
    if (!this.uniqueSelection || !this.selected.find(s => s.documentId === selectedValue.documentId)) {

      if (selectedValue.documentId && this.componentDefinition.fieldDefault) {
        const documentId = this.document?.documentId || this.template?.documentId || `document-id-not-available`;
        const operationName = this.componentDefinition.name + selectedValue.documentId + new Date().getTime();
        const options = { documentId, callerName: this.componentDefinition.name };
        this.pendingOperationService.addPendingOperation(operationName, options);

        try {

          const doc = await this.dataService.getDocument(selectedValue.documentId);

          Object.keys(this.componentDefinition.fieldDefault).map((destFld) => {

            const srcFld = this.componentDefinition.fieldDefault[destFld];

            if (doc[srcFld]) {
              this.notifyDocumentChanged(destFld, doc[srcFld]);
            }

          });

        } catch (error) {
          console.log('error setting default values from related document id: ' + selectedValue.documentId);
        } finally {
          this.pendingOperationService.markOperationComplete(operationName, options);
        }
      }

      const formattedValue = {
        documentId: selectedValue.documentId,
        name: this.relatedDocumentService.getValueFromKeyString(selectedValue, 'systemHeader.summaryName')
      };

      // tslint:disable-next-line:forin
      for (const rkey in this.relatedFields) {
        formattedValue[rkey] = this.relatedDocumentService.getValueFromKeyString(selectedValue, this.relatedFields[rkey]);
      }

      this.selected.push(formattedValue);
    }

    this.inputCtrl.setValue(null);
    this.input.nativeElement.value = '';
    this.input.nativeElement.click();
    this.input.nativeElement.focus();
    this.notifyDocumentChanged(this.fieldName, this.selected);
  }

  clickInput(input: HTMLInputElement, chipsWrapper) {
    try {
      setTimeout(() => {
        chipsWrapper._elementRef.nativeElement.classList.remove('mat-chips-is-not-focused');
        input.focus();
      });
      input.focus();
      this.onClick();
    } catch (err) {
      console.log(err);
    }
  }
  setScrollView(){
    if(this.selectionRows > 0){
      setTimeout(() => {
        const chipListWrapperElem = this.input?.nativeElement?.parentElement;
        let heightScrollFactor = 26;
        if(chipListWrapperElem){
          const childListElems = chipListWrapperElem.children;
          const rows = [];
          const bodyRect = document.body.getBoundingClientRect();
          if(childListElems && childListElems.length > 0){
            // tslint:disable-next-line:prefer-for-of
            for(let i = 0; i < childListElems.length; i++){
              if(childListElems[i].tagName === 'MAT-CHIP'){
                const height = childListElems[i].clientHeight;
                heightScrollFactor = height + 3;
                const posY = (childListElems[i].getBoundingClientRect().top - bodyRect.top);
                if(rows.indexOf(posY) < 0){
                  rows.push(posY);
                }
              }
            }
          }
          if(rows.length > this.selectionRows){
            chipListWrapperElem.style['overflow-y'] = 'scroll';
            chipListWrapperElem.style['max-height'] = (this.selectionRows * heightScrollFactor) + 'px';
          }else{
            chipListWrapperElem.style['overflow-y'] = null;
            chipListWrapperElem.style['max-height'] = null;
          }
        }
        this.detectChanges();
      });
    }
  }


  inputBlur(input: HTMLInputElement, chipsWrapper) {
    try {
      setTimeout(() => {
        chipsWrapper._elementRef.nativeElement.classList.add('mat-chips-is-not-focused');
      });
    } catch (err) {
      console.log(err);
    }

  }

  updateLabelStyle() {
    const newStyle = this.getLabelStyle();
    if (JSON.stringify(newStyle) !== JSON.stringify(this.labelStyle)) {
      this.labelStyle = newStyle;
    }
  }

  getLabelStyle(): object {
    const labelStyleText = this.componentDefinition?.labelStyleText;
    if (!labelStyleText) {
      return {};
    }
    const stylePairs = labelStyleText.split(';')
      .map(style => style.trim())
      .filter(style => style.length > 0);
    return stylePairs.reduce((acc, pair) => {
      const [property, value] = pair.split(':');
      acc[property.trim()] = value.trim();
      return acc;
    }, {});
  }


}
